/**
 * CR310: S0 Scan Settings v2 - AI Deep Scan Tab (Tab 5)
 * CR555: Added Snippet Refinement (S1.4 Pass B) section
 */

import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Brain, Clock, Crop, FileText, Target, Zap } from 'lucide-react';
import {
  type BatchRunFlyerConfig,
  type AiScanModel,
  type PromptPackOption,
  type SnippetRefinementConfig,
  AI_SCAN_MODELS,
} from '../types';

interface AiDeepScanTabProps {
  config: BatchRunFlyerConfig;
  updateConfig: <K extends keyof BatchRunFlyerConfig>(key: K, value: BatchRunFlyerConfig[K]) => void;
  updateSnippetRefinement: <K extends keyof SnippetRefinementConfig>(key: K, value: SnippetRefinementConfig[K]) => void;
  promptPacks: PromptPackOption[];
  loadingPacks: boolean;
  disabled?: boolean;
}

export function AiDeepScanTab({
  config,
  updateConfig,
  updateSnippetRefinement,
  promptPacks,
  loadingPacks,
  disabled = false,
}: AiDeepScanTabProps) {
  const sr = config.snippetRefinement;
  return (
    <div className="space-y-6">
      {/* AI Vision Scan Toggle */}
      <div className="flex items-center justify-between py-2">
        <div className="flex items-center gap-3">
          <Brain className="h-5 w-5 text-muted-foreground" />
          <div>
            <Label className="text-sm font-medium">AI Vision Scan (Candidate Pages)</Label>
            <p className="text-xs text-muted-foreground">
              Use AI Vision to analyze candidate pages for deal extraction
            </p>
          </div>
        </div>
        <Switch
          checked={config.aiEnabled}
          onCheckedChange={(v) => updateConfig('aiEnabled', v)}
          disabled={disabled}
        />
      </div>

      {/* AI Model Selection */}
      <div className="space-y-2 pt-2 border-t">
        <Label className="text-xs flex items-center gap-1">
          <Zap className="h-3 w-3" />
          AI Model
        </Label>
        <Select
          value={config.aiModel}
          onValueChange={(v) => updateConfig('aiModel', v as AiScanModel)}
          disabled={disabled || !config.aiEnabled}
        >
          <SelectTrigger className={`h-9 ${!config.aiEnabled ? "opacity-60" : ""}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {AI_SCAN_MODELS.map((model) => (
              <SelectItem key={model.value} value={model.value}>
                {model.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          Cheaper models may miss some deals. Pro models are more accurate but slower.
        </p>
      </div>

      {/* Prompt Pack Selector */}
      <div className="space-y-2 pt-2 border-t">
        <Label className="text-xs flex items-center gap-1">
          <FileText className="h-3 w-3" />
          Prompt Pack (Step 1.4 Deep Scan)
        </Label>
        <Select
          value={config.promptPackId || "none"}
          onValueChange={(v) => updateConfig('promptPackId', v === "none" ? null : v)}
          disabled={disabled || !config.aiEnabled || loadingPacks}
        >
          <SelectTrigger className={`h-9 ${!config.aiEnabled ? "opacity-60" : ""}`}>
            <SelectValue placeholder={loadingPacks ? "Loading..." : "Select prompt pack"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">
              <span className="text-muted-foreground">(Use inline prompt - not recommended)</span>
            </SelectItem>
            {promptPacks.map((pack) => (
              <SelectItem key={pack.id} value={pack.id}>
                {pack.name} v{pack.version} {pack.is_active && "(Active)"}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          {config.promptPackId 
            ? "AI extraction will use this prompt pack's EXTRACT or SCAN prompt."
            : "⚠️ No prompt pack selected — Step 1.4 will use a hardcoded fallback prompt."}
        </p>
      </div>

      {/* Max AI Calls */}
      <div className="space-y-2 pt-2 border-t">
        <Label className="text-xs flex items-center gap-1">
          <Target className="h-3 w-3" />
          Max AI Calls
        </Label>
        <Input
          type="number"
          min={1}
          max={100}
          value={config.maxAiCalls}
          onChange={(e) => updateConfig('maxAiCalls', parseInt(e.target.value) || 50)}
          disabled={disabled || !config.aiEnabled}
          className={`h-8 text-xs w-24 ${!config.aiEnabled ? "opacity-60" : ""}`}
        />
        <p className="text-xs text-muted-foreground">
          Maximum number of AI calls per batch run (budget control)
        </p>
      </div>

      {/* AI Call Timeout */}
      <div className="space-y-2 pt-2 border-t">
        <Label className="text-xs flex items-center gap-1">
          <Clock className="h-3 w-3" />
          AI Call Timeout (seconds)
        </Label>
        <Input
          type="number"
          min={10}
          max={180}
          step={5}
          value={config.aiTimeoutSeconds}
          onChange={(e) => {
            const seconds = Math.max(10, Math.min(180, parseInt(e.target.value) || 90));
            updateConfig('aiTimeoutSeconds', seconds);
          }}
          disabled={disabled || !config.aiEnabled}
          className={`h-8 text-xs w-24 ${!config.aiEnabled ? "opacity-60" : ""}`}
        />
        <p className="text-xs text-muted-foreground">
          Max time per AI page scan (10–180s). Default 90s. Increase for large/complex pages.
        </p>
      </div>

      {/* Input Mode */}
      <div className="space-y-2 pt-2 border-t">
        <Label className="text-xs">Input Mode</Label>
        <Select
          value={config.inputMode || 'full_page'}
          onValueChange={(v) => updateConfig('inputMode', v as 'full_page' | 'crops_only' | 'both')}
          disabled={disabled || !config.aiEnabled}
        >
          <SelectTrigger className={`h-8 text-xs w-48 ${!config.aiEnabled ? "opacity-60" : ""}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="full_page">Full Page</SelectItem>
            <SelectItem value="crops_only">Crops Only</SelectItem>
            <SelectItem value="both">Both (Full Page + Crops)</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          What to send to AI: full page images, locator crops only, or both
        </p>
      </div>

      {/* CR555: Snippet Refinement (S1.4 Pass B) */}
      <div className="space-y-4 pt-4 border-t">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Crop className="h-5 w-5 text-muted-foreground" />
            <div>
              <Label className="text-sm font-medium">Snippet Refinement (S1.5 – Cropper Pass B)</Label>
              <p className="text-xs text-muted-foreground">
                Controls whether Pass B refines the locator's initial bounding box
              </p>
            </div>
          </div>
          <Switch
            checked={sr.enabled}
            onCheckedChange={(v) => updateSnippetRefinement('enabled', v)}
            disabled={disabled}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label className="text-xs">Min BBox Height Ratio</Label>
            <Input
              type="number"
              min={0.01}
              max={0.2}
              step={0.01}
              value={sr.min_bbox_height_ratio}
              onChange={(e) => updateSnippetRefinement('min_bbox_height_ratio', parseFloat(e.target.value) || 0.03)}
              disabled={disabled || !sr.enabled}
              className={`h-8 text-xs w-24 ${!sr.enabled ? "opacity-60" : ""}`}
            />
            <p className="text-xs text-muted-foreground">
              Minimum Pass A bbox height (as ratio of page) to attempt Pass B
            </p>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Min Evidence for Pass B</Label>
            <Input
              type="number"
              min={0}
              max={10}
              step={1}
              value={sr.min_evidence_for_pass_b}
              onChange={(e) => updateSnippetRefinement('min_evidence_for_pass_b', parseInt(e.target.value) || 2)}
              disabled={disabled || !sr.enabled}
              className={`h-8 text-xs w-24 ${!sr.enabled ? "opacity-60" : ""}`}
            />
            <p className="text-xs text-muted-foreground">
              Minimum evidence hits in Pass A bbox to trigger Pass B
            </p>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Min Evidence for Override</Label>
            <Input
              type="number"
              min={0}
              max={10}
              step={1}
              value={sr.min_evidence_for_override}
              onChange={(e) => updateSnippetRefinement('min_evidence_for_override', parseInt(e.target.value) || 4)}
              disabled={disabled || !sr.enabled}
              className={`h-8 text-xs w-24 ${!sr.enabled ? "opacity-60" : ""}`}
            />
            <p className="text-xs text-muted-foreground">
              Minimum evidence hits for Pass B to override Pass A bbox
            </p>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Max Per Batch</Label>
            <Input
              type="number"
              min={0}
              max={1000}
              step={1}
              value={sr.max_per_batch ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                updateSnippetRefinement('max_per_batch', v === '' ? null : parseInt(v) || null);
              }}
              placeholder="No limit"
              disabled={disabled || !sr.enabled}
              className={`h-8 text-xs w-24 ${!sr.enabled ? "opacity-60" : ""}`}
            />
            <p className="text-xs text-muted-foreground">
              Budget cap: max Pass B calls per batch (blank = unlimited)
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
